<script setup lang="ts">
import { useHubIndexing } from '@/data/Hubs/useHubIndexing'
import { useHubReindexing } from '@/data/Hubs/useHubReindexing'
import {
  hubFileTableContextToken,
  type HubFileTableContext,
} from '@/modules/Workspaces/KnowledgeHub/Hubs/hubFileTableContext'
import { useWorkspaceContext } from '@/modules/Workspaces/useWorkspaceContext'
import { pluralize } from '@/shared/utils/string'
import ConfirmationDialog from '@/uiKit/ConfirmationDialog.vue'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import DotIndicator from '@/uiKit/DotIndicator.vue'
import IconSprite from '@/uiKit/IconSprite.vue'
import { computed, inject, ref } from 'vue'

const props = defineProps<{ hubId: string }>()

const { workspaceId } = useWorkspaceContext()
const { isStale, isProcessing, isIndexing, issueCount, isStateLoaded } = useHubIndexing({
  workspaceId: workspaceId,
  hubId: () => props.hubId,
})

const { mutate: reindex } = useHubReindexing({ workspaceId })

// Try to get the hub file table context, but it may not be available (e.g., in HubsList)
const hubFileTableContext = inject<HubFileTableContext | null>(hubFileTableContextToken, null)

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const isConfirmationDialogOpen = ref(false)

// ---------------------------------------------------------------------------
// Computed properties for button states
// ---------------------------------------------------------------------------

const isIndexable = computed(() => {
  return isStale.value && !isProcessing.value
})

const isReadyWithIssues = computed(() => {
  return issueCount.value > 0 && !isProcessing.value && !isStale.value
})

const canToggleFilter = computed(() => {
  return isReadyWithIssues.value && hubFileTableContext !== null
})

const isButtonClickable = computed(() => {
  return isIndexable.value || canToggleFilter.value
})

const isButtonInert = computed(() => {
  return !isIndexable.value && !canToggleFilter.value
})

const buttonVariant = computed(() => {
  return isReadyWithIssues.value ? 'transparent' : 'neutral'
})

const buttonClass = computed(() => {
  return isButtonClickable.value ? 'cursor-pointer' : 'bg-background-transparent'
})

// ---------------------------------------------------------------------------
// Display text and labels
// ---------------------------------------------------------------------------

const clampedIssueCountLabel = computed(() => {
  const clampedCount = Math.min(issueCount.value, 10)
  const label = issueCount.value > clampedCount ? `${clampedCount}+` : `${clampedCount}`
  return label + ' ' + pluralize(clampedCount, 'file', 'files')
})

const indicatorReason = computed(() => {
  if (issueCount.value > 0) {
    return `${issueCount.value} ${pluralize(issueCount.value, 'file', 'files')} with issues`
  }
  return null
})

// ---------------------------------------------------------------------------
// Event handlers
// ---------------------------------------------------------------------------

function onClick() {
  if (canToggleFilter.value && hubFileTableContext) {
    // When ready with errors and in a file table context, toggle the error filter
    hubFileTableContext.filterErrors.value = !hubFileTableContext.filterErrors.value
  } else if (issueCount.value > 0 && isIndexable.value) {
    // Show confirmation dialog for re-indexing with issues
    isConfirmationDialogOpen.value = true
  } else if (isIndexable.value) {
    // Direct re-index without issues
    reindex({ hubId: props.hubId })
  }
}

function onConfirm() {
  isConfirmationDialogOpen.value = false
  reindex({ hubId: props.hubId })
}
</script>

<template>
  <DarwinButton
    v-if="isStateLoaded"
    size="sm"
    rounded
    :inert="isButtonInert"
    :variant="buttonVariant"
    :class="buttonClass"
    @click.stop.prevent="onClick"
  >
    <template #leading-icon>
      <DotIndicator
        v-if="isProcessing || isIndexing"
        color="purple"
      />
      <IconSprite
        v-else-if="isStale"
        size="sm"
        icon="switch"
        class="text-text-error"
      />
      <DotIndicator
        v-else-if="indicatorReason"
        color="yellow"
        :tooltip="{ title: indicatorReason, placement: 'top' }"
      />
      <IconSprite
        v-else
        size="sm"
        icon="check-circle-fill"
        class="text-text-success"
      />
    </template>
    <template #default>
      <template v-if="isIndexing">Indexing…</template>
      <template v-else-if="isProcessing">Processing…</template>
      <template v-else-if="isStale">Re-index</template>
      <template v-else-if="issueCount > 0">
        Ready ({{ clampedIssueCountLabel }} with issues)
      </template>
      <template v-else>Ready</template>
    </template>
  </DarwinButton>

  <ConfirmationDialog
    title="Re-index with missing files?"
    :description="`Index will not include ${issueCount} ${pluralize(issueCount, 'file', 'files')} with issues.`"
    :open="isConfirmationDialogOpen"
    confirm-text="Re-index"
    variant="black"
    @confirm="onConfirm"
    @close="isConfirmationDialogOpen = false"
    @cancel="isConfirmationDialogOpen = false"
  />
</template>
